import { type ReactElement, type ReactNode } from 'react';

import clivLogo from '@/assets/login/cliv-logo.png';
import clivVideoDesktop from '@/assets/login/cliv-video.mp4';
import loginPageDesktopStill from '@/assets/login/login-page.png';
import loginPageTabletBg from '@/assets/login/tablet-login-page.png';

import './auth-brand-shell.css';

export interface AuthBrandShellProps {
  /** `data-page` for diagnostics and styling hooks. */
  page: string;
  /** Accessible title for the hero region (visible headline stays Control-Live). */
  heroAccessibilityTitle: string;
  /** Message area below the Control-Live headline (login CTAs, unauthorized panel, etc.). */
  children: ReactNode;
}

/**
 * Shared CLIV marketing shell: Soleil font, desktop video / tablet image, corner logo, Control-Live headline.
 */
export function AuthBrandShell({
  page,
  heroAccessibilityTitle,
  children,
}: AuthBrandShellProps): ReactElement {
  return (
    <main
      data-page={page}
      className="auth-brand-shell-root flex min-h-screen w-full items-center justify-center bg-[#e4e6e6] px-[50px] max-lg:items-start max-lg:justify-start max-lg:p-0"
    >
      <div className="relative flex h-[80vh] w-[95%] flex-col items-stretch bg-cover bg-center bg-no-repeat max-lg:h-full max-lg:min-h-screen max-lg:w-full">
        <div className="pointer-events-none absolute inset-0 hidden overflow-hidden lg:block" aria-hidden>
          <div
            className="absolute inset-0 bg-cover bg-center opacity-0 motion-reduce:opacity-100"
            style={{ backgroundImage: `url(${loginPageDesktopStill})` }}
          />
          <video
            className="absolute inset-0 h-full w-full object-cover motion-reduce:hidden"
            autoPlay
            muted
            playsInline
            loop
            preload="metadata"
          >
            <source src={clivVideoDesktop} type="video/mp4" />
          </video>
        </div>
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat lg:hidden"
          style={{ backgroundImage: `url(${loginPageTabletBg})`, minHeight: '100%' }}
          aria-hidden
        />

        <img
          src={clivLogo}
          alt=""
          className="absolute left-0 top-0 z-10 w-[10%] min-w-[3.5rem] max-w-[10rem] -translate-x-1/2 -translate-y-1/2 border-0 max-lg:left-[1.3rem] max-lg:top-[1.3rem] max-lg:w-[18%] max-lg:translate-none max-[500px]:left-0 max-[500px]:top-0 max-[500px]:w-[26.5%] max-[500px]:translate-x-[35%] max-[500px]:translate-y-[35%]"
          width={120}
          height={120}
        />

        <div className="relative z-[1] flex min-h-0 w-full flex-1 flex-col items-center px-[3vw] max-lg:px-[15vw] max-[500px]:px-[9vw]">
          <div
            className="mx-auto mb-4 mt-14 flex w-[45%] shrink-0 flex-col items-center max-lg:mb-4 max-lg:mt-10 max-lg:w-full max-lg:items-start max-lg:leading-[4rem] max-[500px]:mb-4 max-[500px]:mt-14 max-[500px]:items-start max-[500px]:leading-normal max-[500px]:pt-0 lg:mt-32"
            aria-labelledby={`${page}-hero-heading`}
          >
            <h1 id={`${page}-hero-heading`} className="sr-only">
              {heroAccessibilityTitle}
            </h1>
            <span className="text-center text-[clamp(1.6rem,3.25vw,2.75rem)] font-bold uppercase leading-tight text-[#FFFFFF] max-lg:text-[9.25vw] max-lg:whitespace-normal max-[500px]:text-left max-[500px]:text-[13vw]">
              Control-Live
            </span>
            <span className="mt-1 hidden text-center text-[clamp(1rem,2.5vw,2rem)] font-light uppercase leading-tight text-[#FFFFFF] lg:inline">
              wherever you are in the world
            </span>
            <span className="mt-1 inline text-left text-[clamp(1rem,7.8vw,2rem)] font-light uppercase leading-tight text-[#FFFFFF] lg:hidden">
              wherever <br />
              you are in <br />
              the <br />
              world
            </span>
          </div>

          <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-start pb-10 pt-1">
            <div className="flex w-full flex-col items-center gap-4">{children}</div>
          </div>
        </div>
      </div>
    </main>
  );
}
